import axios, { AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types/api.ts';

// Import shared API types from the rest-api-types package
import type { 
    Group,
    User as APIUserType,
    APIAddGroupReqBody,
    APIUpdateGroupReqBody,
    APIAddUserReqBody,
    APIUpdateUserReqBody,
    APISetActiveReqBody,
    APIDeleteReqBody,
    UserPermission
} from '@spona/rest-api-types';

// User response type with _id (API returns this)
export interface User extends APIUserType {
    _id: string;
}

// ==================== USER API CLASS ====================

const logger = {
    debug: (...args: any[]) => console.debug('[UserAPI]', ...args),
    log: (...args: any[]) => console.log('[UserAPI]', ...args),
    error: (...args: any[]) => console.error('[UserAPI]', ...args),
};

export class UserAPI {
    /**
     * Get all users
     * @param includeArchived - If true, includes archived users in results
     */
    async getAll(includeArchived: boolean = false): Promise<ApiResponse<User[]>> {
        try {
            logger.debug('Fetching all users...', 'includeArchived:', includeArchived);
            const response: AxiosResponse<User[]> = await axios.get('/api/user/all', {
                params: { includeArchived: includeArchived.toString() }
            });
            logger.log('Fetched users:', response.data.length);
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            logger.error('Failed to fetch users:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch users'
            };
        }
    }

    /**
     * Get current user info
     */
    async getCurrent(): Promise<ApiResponse<User>> {
        try {
            logger.debug('Fetching current user...');
            const response: AxiosResponse<User> = await axios.get('/api/user/get');
            logger.log('Current user:', response.data);
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            logger.error('Failed to fetch current user:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch current user'
            };
        }
    }

    /**
     * Create a new user
     */
    async create(userData: APIAddUserReqBody): Promise<ApiResponse<any>> {
        try {
            logger.debug('=== CREATE USER API CALL START ===');
            logger.debug('Request data:', JSON.stringify(userData, null, 2));
            
            const response = await axios.post('/api/user/add', userData);
            
            logger.log('User created successfully:', response.data);
            logger.debug('=== CREATE USER API CALL END ===');
            
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            logger.error('=== CREATE USER API CALL ERROR ===');
            logger.error('Error response:', error.response?.data);
            logger.error('Error message:', error.message);
            logger.error('Error status:', error.response?.status);
            
            return {
                success: false,
                error: error.response?.data || error.message || 'Failed to create user'
            };
        }
    }

    /**
     * Update an existing user
     */
    async update(userData: APIUpdateUserReqBody): Promise<ApiResponse<User>> {
        try {
            const response = await axios.post<User>('/api/user/update', userData);
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update user'
            };
        }
    }

    /**
     * Set user active status
     */
    async setActive(request: APISetActiveReqBody): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/user/set-active', request);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update user status'
            };
        }
    }

    /**
     * Delete a user
     * @deprecated Use archive() instead
     */
    async delete(request: APIDeleteReqBody): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/user/delete', request);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to delete user'
            };
        }
    }

    /**
     * Archive a user (soft delete)
     * @param userId - The ID of the user to archive
     */
    async archive(userId: string): Promise<ApiResponse<User>> {
        try {
            logger.debug('Archiving user:', userId);
            const response: AxiosResponse<User> = await axios.post(`/api/user/${userId}/archive`);
            logger.log('User archived successfully');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            logger.error('Failed to archive user:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to archive user'
            };
        }
    }

    /**
     * Unarchive a user (restore from archive)
     * @param userId - The ID of the user to unarchive
     */
    async unarchive(userId: string): Promise<ApiResponse<User>> {
        try {
            logger.debug('Unarchiving user:', userId);
            const response: AxiosResponse<User> = await axios.post(`/api/user/${userId}/unarchive`);
            logger.log('User unarchived successfully');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            logger.error('Failed to unarchive user:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to unarchive user'
            };
        }
    }

    /**
     * Get all groups
     */
    async getAllGroups(): Promise<ApiResponse<Group[]>> {
        try {
            const response: AxiosResponse<Group[]> = await axios.get('/api/user/groups');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch groups'
            };
        }
    }

    /**
     * Update user permissions
     * @param userId - The ID of the user to update
     * @param permissions - Array of permissions to set (backend determines if custom or default)
     */
    async updatePermissions(userId: string, permissions: UserPermission[]): Promise<ApiResponse<{ isCustom: boolean; permissions: UserPermission[] }>> {
        try {
            logger.debug('Updating permissions for user:', userId, 'with', permissions.length, 'permissions');
            const response: AxiosResponse<{ success: boolean; isCustom: boolean; permissions: UserPermission[] }> = 
                await axios.post('/api/user/update-permissions', { userId, permissions });
            logger.log('Permissions updated successfully');
            return {
                success: true,
                data: {
                    isCustom: response.data.isCustom,
                    permissions: response.data.permissions
                }
            };
        } catch (error: any) {
            logger.error('Failed to update permissions:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update permissions'
            };
        }
    }

    async resetPassword(userId: string, newPassword: string): Promise<ApiResponse<void>> {
        try {
            await axios.post(`/api/user/${userId}/reset-password`, { password: newPassword });
            return { success: true };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to reset password'
            };
        }
    }
}

// ==================== GROUP API CLASS ====================

export class GroupAPI {
    /**
     * Get all groups
     */
    async getAll(): Promise<ApiResponse<Group[]>> {
        try {
            const response: AxiosResponse<Group[]> = await axios.get('/api/group/all');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch groups'
            };
        }
    }

    /**
     * Create a new group
     */
    async create(groupData: APIAddGroupReqBody): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/group/add', groupData);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to create group'
            };
        }
    }

    /**
     * Update an existing group
     */
    async update(groupData: APIUpdateGroupReqBody): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/group/update', groupData);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update group'
            };
        }
    }

    /**
     * Set group active status
     */
    async setActive(request: APISetActiveReqBody): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/group/set-active', request);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update group status'
            };
        }
    }

    /**
     * Delete a group
     */
    async delete(request: APIDeleteReqBody): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/group/delete', request);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to delete group'
            };
        }
    }
}

// ==================== EXPORTS ====================

// Create singleton instances
export const userApi = new UserAPI();
export const groupApi = new GroupAPI();

// Default export for convenience
export default userApi;